import {
  Box,
  Container,
  Card,
  CardBody,
  Flex,
  Icon,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  useDisclosure,
  Collapse,
  Portal,
  Button
} from '@chakra-ui/react';
import { useAuth } from '../../lib/auth/AuthProvider';
import { supabase } from '../../lib/supabase';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/v0/PageLayout';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

// 引入拆分后的组件
import { UserProfileCard } from '../../components/v0/me/UserProfileCard';
import { StatisticsCards } from '../../components/v0/me/StatisticsCards';
import { ExpandedStatistics } from '../../components/v0/me/ExpandedStatistics';
import { ActionButtons } from '../../components/v0/me/ActionButtons';
import { SettingsSection } from '../../components/v0/me/SettingsSection';

// 引入自定义钩子
import { useUserStats } from '../../hooks/v0/me/useUserStats';

export const Me = () => {
  const { user } = useAuth();
  const maxW = useBreakpointValue({ base: '100%', md: '90%', lg: '1200px' });
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation(['v0/common', 'v0/me']);
  const scrollRef = useRef(null);

  // 使用自定义钩子获取统计数据
  const {
    projects,
    tasks,
    today,
    totalProjects,
    totalTasks,
    completedTasks,
    completionRate,
    urgentTasks,
    vitalTasks,
    getActivityData,
    getProductivityData,
    getProductivityInsights
  } = useUserStats();

  // 状态
  const { isOpen: isStatsExpanded, onToggle: toggleStats } = useDisclosure();

  // 主题颜色
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = 'teal.500';
  const subtleColor = useColorModeValue('gray.100', 'gray.700');

  // 登出
  const handleSignOut = async () => {
    // 使用强制登出工具函数，确保即使在离线状态下也能登出
    import('../../utils/authUtils').then(({ forceSignOut }) => {
      forceSignOut(
        navigate,
        toast,
        t('v0/common:actions.logout'),
        t('v0/common:errors.logoutFailed', '退出失败')
      );
    }).catch(error => {
      console.error('加载 authUtils 失败:', error);
      // 回退到原始登出逻辑
      try {
        supabase.auth.signOut().catch(console.error);
        toast({
          title: t('v0/common:actions.logout'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('登出失败:', error);
      } finally {
        // 无论如何都清理本地 session/token
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (e) {
          // 忽略本地清理异常
        }
        navigate('/login');
      }
    });
  };

  // 准备数据
  const activityData = getActivityData();
  const productivityData = getProductivityData();
  const productivityInsights = getProductivityInsights();

  return (
    <PageLayout title={t('v0/me:me', '我的')}>
      <Container maxW={maxW} py={4} ref={scrollRef}>
        {/* 用户信息卡片 */}
        <UserProfileCard
          user={user}
          onSignOut={handleSignOut}
          cardBg={cardBg}
          borderColor={borderColor}
          accentColor={accentColor}
        />

        {/* 统计卡片 */}
        <StatisticsCards
          totalProjects={totalProjects}
          totalTasks={totalTasks}
          completedTasks={completedTasks}
          completionRate={completionRate}
          urgentTasks={urgentTasks}
          vitalTasks={vitalTasks}
          accentColor={accentColor}
          subtleColor={subtleColor}
        />

        {/* 展开/折叠按钮 */}
        <Card
          borderRadius="lg"
          mb={4}
          variant="outline"
          boxShadow="none"
          cursor="pointer"
          onClick={toggleStats}
        >
          <CardBody p={2}>
            <Flex
              alignItems="center"
              justifyContent="center"
              color="gray.500"
            >
              <Text fontSize="sm" fontWeight="medium">
                {isStatsExpanded
                  ? t('v0/me:stats.collapse')
                  : t('v0/me:stats.expand')}
              </Text>
              <Icon
                as={isStatsExpanded ? FiChevronUp : FiChevronDown}
                ml={1}
              />
            </Flex>
          </CardBody>
        </Card>

        {/* 扩展统计区域 */}
        <Collapse in={isStatsExpanded} animateOpacity>
          <ExpandedStatistics
            activityData={activityData}
            productivityData={productivityData}
            productivityInsights={productivityInsights}
            tasks={tasks}
            accentColor={accentColor}
          />
        </Collapse>

        {/* 操作按钮区 */}
        <ActionButtons onSignOut={handleSignOut} />

        {/* 设置部分 */}
        <SettingsSection />
      </Container>
    </PageLayout>
  );
};